import {
  DEEP_LINK_PREFIX,
  deepLinkRegistry,
  type DeepLinkTarget,
} from './deepLinkRegistry';

/**
 * Resolve an incoming link into an internal navigation target, or `null` if it
 * is not a recognised app deep link.
 *
 * Accepts every shape a link can reach us in:
 *   - Universal Link / App Link:  https://www.pensionbee.com/app/contributions
 *   - Custom scheme (future):     pensionbee://app/contributions
 *   - Iterable push `openUrl`:    (either of the above, from the payload)
 *   - Bare path:                  /app/contributions  or  /uk/app/contributions
 *
 * The `/uk` basePath and the `/app` prefix are both tolerated and stripped, so
 * the caller never has to normalise before calling this.
 */
export function resolveDeepLink(input: string): DeepLinkTarget | null {
  const slug = extractSlug(input);
  if (!slug) return null;
  return deepLinkRegistry[slug as keyof typeof deepLinkRegistry] ?? null;
}

/** Pull the registry slug out of any supported link shape. */
function extractSlug(input: string): string | null {
  if (!input) return null;

  let pathname = input;
  try {
    const url = new URL(input);
    pathname =
      url.protocol === 'http:' || url.protocol === 'https:'
        ? url.pathname
        : // Custom scheme, e.g. pensionbee://app/contributions — the "app"
          // segment is parsed as the host, so fold host + pathname back
          // together into a normal path.
          `/${url.host}${url.pathname}`;
  } catch {
    // Not a full URL (bare path) — use as-is.
  }

  const segments = pathname.split('/').filter(Boolean);

  // Tolerate the `/uk` basePath if it is present.
  if (segments[0] === 'uk') segments.shift();

  // Must be namespaced under the deep-link prefix.
  if (segments.shift() !== DEEP_LINK_PREFIX) return null;

  return segments.join('/') || null;
}
